import { Alert } from '@api';
import { Button } from '@design/components/button/Button';
import { DropdownMenu } from '@design/components/dropdown-menu/DropdownMenu';
import {
  DropdownEntry,
  DropdownGroup,
} from '@design/components/dropdown-menu/dropdown-group/DropdownGroup';
import { useInsightDrawer } from '@terminal/hooks/use-insight-drawer/useInsightDrawer';
import { useNavigateToKgInsight } from '@terminal/hooks/use-navigate-to-kg-insight/useNavigateToKgInsight';
import { useConversationTask } from '@terminal/react-query-hooks/alfred/useConversationTask';
import { usePostRead } from '@terminal/react-query-hooks/events/use-event-alerts/usePostRead';
import {
  POSTHOG_TRACKING_SOURCES,
  PosthogTrackingSource,
} from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { useChatNavigation } from '@terminal/views/alfred/use-chat-navigation/useChatNavigation';
import clsx from 'clsx';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './AlertsCard.module.scss';
import { AlertsAlfredCard } from './components/alerts-alfred-card/AlertsAlfredCard';
import { AlertsInsightCard } from './components/alerts-insight-card/AlertsInsightCard';
import { AlertsKgInsightCard } from './components/alerts-kg-insight-card/AlertsKgInsightCard';
import { getAlfredRequestId } from './utils/getAlfredRequestId';
import { useKgInsightCardData } from './utils/useKgInsightCardData';
import { useTrackingDropdownItem } from './utils/useTrackingDropdownItem';

export interface AlertsCardProps {
  alertEvent: Alert;
  onNavigate?: () => void;
  trackingSource?: PosthogTrackingSource;
  compact?: boolean;
}

export const AlertsCard = ({
  alertEvent,
  onNavigate,
  trackingSource = POSTHOG_TRACKING_SOURCES.ALERTS,
  compact = false,
}: AlertsCardProps) => {
  const { t } = useTranslation('alerts', { useSuspense: false });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { mutate: markAsRead } = usePostRead();
  const { openDrawer } = useInsightDrawer();
  const { navigateToKgInsight } = useNavigateToKgInsight();
  const { navigateToConversation, setAlfredTrackingSource } =
    useChatNavigation();
  const { getAlertProperties } = usePosthogEvents();
  const { read, source_type: sourceType } = alertEvent;
  const initiatedMarkAsReadIdRef = useRef<string | null>(null);
  if (read) {
    initiatedMarkAsReadIdRef.current = null;
  }
  const isNewEarningsTabEnabled = useFlags(state =>
    state.getFeatureFlagState('PLATFORM-4671-ao-earnings')
  );
  const isAlfredCard = sourceType === 'alfred';
  const isKgInsightAlert = sourceType === 'kg-insight';
  const {
    data: alfredConversationData,
    isLoading: isAlfredConversationLoading,
  } = useConversationTask(alertEvent.source_id, isAlfredCard);
  const {
    category: kgInsightCategory,
    entity: kgInsightEntity,
    entityTag: kgInsightEntityTag,
    insight: kgInsight,
    isKgInsightCard,
    isLoading: isKgInsightCardLoading,
    shouldLoadKgInsight,
  } = useKgInsightCardData({
    alertEvent,
    isNewEarningsTabEnabled,
  });
  const kgInsightCardCategory = kgInsight
    ? kgInsightCategory
    : alertEvent.metadata?.category;
  const alfredRequestId = getAlfredRequestId(alertEvent);
  const alfredTask =
    alfredConversationData?.tasks?.find(
      task => task.request_id === alfredRequestId
    ) ?? alfredConversationData?.tasks?.[0];
  const trackingDropdownItem = useTrackingDropdownItem({
    alertEvent,
    isKgInsightCard,
    kgInsightEntityTag,
    trackingSource,
  });
  const shouldHideKgInsightAlert =
    isKgInsightAlert &&
    (!shouldLoadKgInsight ||
      (!isKgInsightCardLoading &&
        (!isKgInsightCard || !kgInsightEntity || !kgInsight)));

  if (isAlfredCard && !isAlfredConversationLoading && !alfredTask?.last_state) {
    return null;
  }

  if (shouldHideKgInsightAlert) {
    return null;
  }

  const handleCardClick = () => {
    if (shouldLoadKgInsight && !kgInsightCategory) {
      return;
    }

    if (!read && initiatedMarkAsReadIdRef.current !== alertEvent.id) {
      initiatedMarkAsReadIdRef.current = alertEvent.id;
      markAsRead([alertEvent.id]);
    }

    if (isAlfredCard) {
      setAlfredTrackingSource(trackingSource);
      navigateToConversation(alertEvent.source_id, {
        trackingSource,
      });
    } else if (shouldLoadKgInsight && kgInsightCategory) {
      navigateToKgInsight({
        source: trackingSource,
        category: kgInsightCategory,
        insightId: alertEvent.source_id,
      });
    } else {
      openDrawer({
        id: alertEvent.source_id,
        entityTag: alertEvent.metadata?.entity_tag,
        trackingProperties: {
          source: trackingSource,
          ...getAlertProperties(alertEvent),
        },
      });
    }

    onNavigate?.();
  };

  const dropdownItems = [
    [
      {
        label: read ? t('alerts:markAsUnread') : t('alerts:markAsRead'),
        key: 'read',
        onClick: () => markAsRead([alertEvent.id]),
      },
      trackingDropdownItem,
    ].filter((item): item is DropdownEntry => item !== undefined),
  ];

  const dropdown = (
    <div onClick={e => e.stopPropagation()}>
      <DropdownMenu
        triggerNode={
          <Button variant="empty" size="xsmall" iconName="Ellipsis" />
        }
        align="end"
        onOpenChange={setIsDropdownOpen}
        contentProps={{
          style: {
            borderRadius: '8px',
            minWidth: 'unset',
            width: 'fit-content',
            backgroundColor: 'var(--background-soft)',
          },
        }}
      >
        {dropdownItems.map((group, key) => (
          <DropdownGroup items={group} key={key} />
        ))}
      </DropdownMenu>
    </div>
  );

  const renderCardContent = () => {
    if (isAlfredCard) {
      return (
        <AlertsAlfredCard
          alertEvent={alertEvent}
          conversationData={alfredConversationData}
          dropdown={dropdown}
          isLoading={isAlfredConversationLoading}
        />
      );
    }

    if (!shouldLoadKgInsight) {
      return (
        <AlertsInsightCard
          alertEvent={alertEvent}
          dropdown={dropdown}
          compact={compact}
        />
      );
    }

    return (
      <AlertsKgInsightCard
        alertEvent={alertEvent}
        dropdown={dropdown}
        entity={kgInsightEntity}
        insight={kgInsight}
        category={kgInsightCardCategory}
        isLoading={isKgInsightCardLoading}
      />
    );
  };

  return (
    <div
      className={clsx(styles.root, {
        [styles.read]: read,
        [styles.isDropdownOpen]: isDropdownOpen,
      })}
      data-testid="alerts-card"
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleCardClick();
        }
      }}
    >
      <div className={styles.contentWrapper}>{renderCardContent()}</div>
    </div>
  );
};
